import * as React from 'react'
import {
  Drawer, Box, Typography, IconButton, Button, TextField, MenuItem, Chip, Tooltip, Alert,
  RadioGroup, Radio, FormControlLabel, Checkbox, InputAdornment,
  Table, TableHead, TableBody, TableRow, TableCell, TablePagination,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AcUnitOutlinedIcon from '@mui/icons-material/AcUnitOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'

import {primaryBtnSx, secondaryBtnSx, headerCellSx, cellSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, PRIMARY_BLUE, WARNING, SUCCESS, withOpacity, ERROR} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'

import {kg, int, dims, grams} from '../format'
import {FREEZE_LEVEL} from '../data/reference'
import {POLICY} from '../data/sopCopy'
import {
  DWS_SLOTS, DWS_VIDEO_TYPES, DWS_IMAGE_TYPES, DWS_MAX_VIDEO_BYTES, DWS_MAX_IMAGE_BYTES, mb,
} from '../data/dwsCopy'
import {volumetricKg, chargeableKg, withinTolerance, getVolumetricDivisor} from '../weightMath'
import {PACK_KIND_LABEL, packOptionLabel, brandingLabel} from '../packing/state/packingModel'
import {validateVideo, validateImage, buildEvidenceFiles, rawEvidenceFiles} from '../state/dwsUpload'
import {
  LEVELS, commonest, deriveProposal, weightFacts, weightedAvg,
  dwsPartition, gapsFor, gapSentence, subjectFor, DECLARATION,
  hasAttachedEvidence, evidenceScope, EVIDENCE_STATE,
} from './request'
import IniGateList from './IniGateList'
import CartonSVG from '../components/CartonSVG'
import SleeveSVG from '../components/SleeveSVG'
import DropZone from '../components/DropZone'
import KeyValue from '../components/KeyValue'
import {chipSx} from '../components/tone'

const PER_PAGE = 5

/** The extensions, not the MIME types — two entries share 'JPG', so this dedupes. */
function formatWords(types) {
  const words = Array.from(new Set(Object.keys(types).map((t) => types[t])))
  return words.slice(0, -1).join(', ') + ' or ' + words[words.length - 1]
}

const VIDEO_FORMATS = formatWords(DWS_VIDEO_TYPES)
const IMAGE_FORMATS = formatWords(DWS_IMAGE_TYPES)

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}
const sectionLabelSx = {fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY, mb: 0.75}
const alertSx = {fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.75, '& .MuiAlert-message': {lineHeight: 1.55}}
const subtleTextSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}

/** Every field in this drawer carries a hint saying where its number comes from
 * or what it commits the merchant to, so the helper line is styled once. */
// MUI draws the required asterisk in the label's own colour — the same grey
// as the label text, which reads as punctuation rather than a marker.
const requiredAsteriskSx = {'& .MuiFormLabel-asterisk': {color: ERROR}}

const fieldSx = {
  ...requiredAsteriskSx,
  fontFamily: FONT,
  fontSize: SIZE.xs,
  '& .MuiFormHelperText-root': {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, ml: 0.25, mr: 0},
  // Project rule 16: MUI leaks theme.typography.body1's 16px into these two,
  // and it beats inheritance, so the root fontSize above never reaches them.
  // Beaten at the call site because the shared constant cannot be edited.
  '& .MuiInputBase-input': {fontFamily: FONT, fontSize: SIZE.xs},
  '& .MuiSelect-select': {fontFamily: FONT, fontSize: SIZE.xs},
}

/**
 * A Select's menu is PORTALLED to document.body, so it renders outside this
 * drawer's subtree and `fieldSx`'s descendant selectors — which reach only the
 * closed trigger — never touch it. Left alone its items take
 * theme.typography.body1's 1rem, the same leak `fieldSx` beats one level
 * further out, and the list opens a third larger than every other dropdown in
 * the module. Same shape and same SIZE['2xs'] as IniFilterBar's own menus, so
 * an open menu here matches every other menu a merchant meets in freeze-v2.
 */
const selectMenuProps = {
  PaperProps: {sx: {'& .MuiMenuItem-root': {fontFamily: FONT, fontSize: SIZE['2xs']}}},
}

/**
 * `cellSx` is the app-wide table cell, at SIZE.base — a page-table size that
 * reads a third larger than everything around it inside a drawer, including the
 * SKU label in the same row. Pinned to the drawers' own body size here rather
 * than in the shared constant, which three modules read.
 */
const drawerCellSx = {...cellSx, fontSize: SIZE['2xs']}

/** 'row(s)' is developer shorthand; a merchant should read one or the other. */
function rowWord(n) {
  return n === 1 ? 'row' : 'rows'
}

/**
 * Which of the two weights decides the bill, AND the figure it decides it at —
 * "governs" says nothing useful without the number the chargeable field is
 * expected to hold. Volumetric governing is the case worth colouring: the bill
 * is then being driven by the packing material rather than by the product, and
 * that is the one a merchant can act on by repacking.
 */
const GOVERNS_SENTENCE = {
  dead: (v) => `Dead weight governs at ${kg(v)}`,
  vol: (v) => `Volumetric weight governs at ${kg(v)}`,
  both: (v) => `Dead and volumetric are equal at ${kg(v)}`,
}

/** `evidenceScope`'s typed state → this drawer's chip tone. The prose comes with
 * the state; only the colour is decided here. */
const EVIDENCE_TONE = {
  [EVIDENCE_STATE.OPTIONAL]: 'neutral',
  [EVIDENCE_STATE.REQUIRED]: 'danger',
  [EVIDENCE_STATE.SATISFIED]: 'ok',
}

/**
 * A numbered step heading, the shape the Weight Dispute module's own evidence
 * flow uses. The number is decorative — the heading beside it already names the
 * step — so it is marked `aria-hidden` rather than read out twice.
 *
 * No approval percentage rides on this badge. The dispute screens print "97%
 * approved" / "52% approved" beside the same two steps, but nothing in this
 * repo sources those figures and they describe a different process; a freeze
 * request is a proposal about what a SKU weighs, not a contested bill. The
 * video step says only that it is recommended.
 */
function StepHead({n, title, chip}) {
  return (
    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75}}>
      <Box
        aria-hidden
        sx={{
          width: 20, height: 20, flexShrink: 0, borderRadius: '6px',
          bgcolor: withOpacity(PRIMARY_BLUE, 0.12), color: PRIMARY_BLUE,
          display: 'grid', placeItems: 'center',
          fontFamily: FONT, fontSize: SIZE['2xs'], fontWeight: WEIGHT.bold,
        }}
      >
        {n}
      </Box>
      <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY}}>
        {title}
      </Typography>
      {chip && <Chip size='small' label={chip} sx={chipSx('ok')} />}
    </Box>
  )
}

/** Every SKU row should carry a real sub-category; a mis-catalogued one may not. */
function subOf(row) {
  return row.sub || 'Uncategorised'
}

/**
 * Weight Freeze (v2) — Initiate: the 12-section request drawer.
 *
 * Everything the merchant can still edit lives here as local form state; the
 * NUMBERS — what a level means, what is missing, what gets written — come
 * from `./request`, never recomputed inline. This drawer never calls
 * `buildRequest` itself: `onSubmit` hands the raw payload up to
 * `InitiatePage`, which owns the merchant identity and the shared store's
 * `addRequest`, exactly like every other mutation in this module.
 *
 * SECTION ORDER: the material before the weights, because volumetric weight is
 * anchored to the packing material everywhere in this module (Packing says so
 * in as many words when a material is removed) and the box measured below is
 * the box that material makes. `IniNewSkuDrawer` orders its sections the same
 * way; the two sit one click apart and read as one drawer.
 *
 * DERIVED, NEVER TYPED: volumetric comes from the typed dimensions and
 * chargeable from the higher of dead and volumetric, both live as the merchant
 * types. They used to be two more number fields beside dead weight, which let a
 * merchant propose a chargeable weight their own box contradicted — the reason
 * this drawer carried a mismatch warning and a 'Use 1.30 kg' button. Deriving
 * the pair deletes that whole class of disagreement, and the warning with it.
 *
 * NO DWS RECORD PICKER: this drawer used to carry an "Attach DWS records"
 * section — a checkbox list of the scanner records already on file, which the
 * merchant ticked to send along with the request. It is gone. `gapsFor` never
 * read those ticks — the evidence that decides the gate is the merchant's OWN
 * video and stills, captured in section 10 — and a ticked record reached the
 * reviewer as a bare count on the review card, never as the AWBs themselves:
 * only `RqApprovedDrawer` chips those, and nothing in this module moves a
 * request to `approved`, so no request raised here ever reaches that screen.
 *
 * WHAT WENT WITH IT, stated plainly so nobody re-adds the list believing it is
 * still on screen somewhere: WHICH scanner records back a row is no longer
 * visible anywhere in Initiate. The evidence index this drawer reads holds
 * per-row COUNTS, not records (`dwsEvidence` in ../state/dwsEvidence), and
 * counts are what section 9 reports. Restoring the list would mean importing
 * `dwsRecordsFor` and taking the `uploads` prop back, not reading `evIndex`.
 *
 * MOUNT LIFECYCLE: `InitiatePage` renders this component only while
 * `drawerOpen && picked.length > 0`, so a fresh selection is always a fresh
 * mount — there is no "reset on reopen" effect here the way
 * `DwsUploadDrawer` needs one, because there is never a stale previous visit
 * to clear. The blob urls the evidence steps mint still need an unmount
 * cleanup, because a merchant who attaches a video and then cancels never
 * reaches the submit path that hands those urls on.
 *
 * WIDTH: 820 on a desktop viewport, not the 660 this drawer used to be. It is
 * portalled, so it sits outside the page's `zoom: 0.8` and renders at true
 * size, and the five named photo slots below are the same five `DwsUploadDrawer`
 * lays out — that drawer measured 820 for them. Three across at 660 leaves each
 * slot 204px, which clips "Shipment on weighing scale"; at 820 they get 252px.
 * The `sm` step keeps the old 660 for tablet widths, where the grid drops to
 * two across on its own.
 */
export default function IniRaiseDrawer({open, packs, rows, evIndex, onClose, onSubmit}) {
  const [level, setLevel] = React.useState(FREEZE_LEVEL.SUBCAT)
  const [subcat, setSubcat] = React.useState(() => commonest(rows, 'sub') || 'Uncategorised')
  const [touched, setTouched] = React.useState(false)
  const [dead, setDead] = React.useState('')
  const [len, setLen] = React.useState('')
  const [bre, setBre] = React.useState('')
  const [hgt, setHgt] = React.useState('')
  const [packId, setPackId] = React.useState('')
  const [declared, setDeclared] = React.useState(false)
  const [remarks, setRemarks] = React.useState('')
  const [page, setPage] = React.useState(0)
  const [video, setVideo] = React.useState(null)
  const [images, setImages] = React.useState({})
  const [fileErrors, setFileErrors] = React.useState({})
  const [dragKey, setDragKey] = React.useState(null)

  // Tracked in a ref rather than in state, so the unmount cleanup still sees
  // every url this drawer ever minted even if the last render had dropped it.
  const blobUrls = React.useRef([])
  const trackUrl = (file) => {
    const url = URL.createObjectURL(file)
    blobUrls.current.push(url)
    return url
  }
  const releaseUrl = (url) => {
    if (!url) return
    URL.revokeObjectURL(url)
    blobUrls.current = blobUrls.current.filter((u) => u !== url)
  }
  React.useEffect(() => () => {
    blobUrls.current.forEach((u) => URL.revokeObjectURL(u))
    blobUrls.current = []
  }, [])

  // The subject this request actually covers. At Sub-category level that is
  // a NARROWER set than the merchant ticked — see the info note in section 5
  // below — everywhere else it is every row that was ticked.
  const covered = level === FREEZE_LEVEL.SUBCAT ? rows.filter((r) => subOf(r) === subcat) : rows
  const coverKey = covered.map((r) => r.key).join('|')

  // THE ONE LATCH. Any edit to dead weight, to a dimension or to the pack sets
  // it, and once set this effect never re-derives anything again — a per-field
  // dirty flag would let a level switch silently overwrite a figure the
  // merchant just typed. The dependency array is deliberately `[coverKey]`, a
  // STRING, so this fires when the SET of covered rows changes, not when their
  // object identities do.
  React.useEffect(() => {
    if (touched || !covered.length) return
    const p = deriveProposal(covered, packs)
    setDead(String(p.deadKg))
    setPackId(p.packId)
    // The DECLARED MATERIAL'S OWN BOX is the module's only real dimension
    // source: volumetric weight is anchored to the packing material here (the
    // wording Packing uses when it refuses to delete one), and the store's
    // cascade keeps `pack.volKg` in sync with `pack.dims` through this same
    // `volumetricKg`. So a carton seeds a complete triple and the derived
    // volumetric opens on exactly the figure that material bills at.
    //
    // A flyer or a paper wrap declares no box at all, and NOTHING in this
    // dataset holds a triple behind a row's own `volKg` — it is neither the
    // pack's figure nor any DWS record's carton. Rather than invent one, the
    // triple opens empty for a boxless material and the merchant measures the
    // parcel, exactly as they do in the new-product drawer.
    const box = packs.find((x) => x.id === p.packId)
    const triple = (box && box.dims) || []
    setLen(triple[0] != null ? String(triple[0]) : '')
    setBre(triple[1] != null ? String(triple[1]) : '')
    setHgt(triple[2] != null ? String(triple[2]) : '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coverKey])

  const pack = packs.find((p) => p.id === packId) || null

  // DERIVED, not typed — see the header. `volumetricKg` returns 0 for a triple
  // that is not yet complete, which is why the strip below tests `> 0` rather
  // than falsiness before it prints a figure.
  const dimsTriple = [Number(len) || 0, Number(bre) || 0, Number(hgt) || 0]
  const vol = volumetricKg(dimsTriple)
  const charge = chargeableKg(Number(dead) || 0, vol)
  const DIMENSION_FIELDS = [
    ['Length', len, setLen],
    ['Breadth', bre, setBre],
    ['Height', hgt, setHgt],
  ]

  // `charge` IS the derived figure, so `facts.mismatch` can never fire; only
  // `.governs` and `.higher` are read. Same function the new-product drawer
  // uses, so the governs sentence is one sentence in the codebase, not two.
  const facts = weightFacts(dead, vol, charge)
  const partition = dwsPartition(covered, evIndex)

  // Shaped ONCE per render and reused by both the submit gate and the payload
  // `handleSubmit` hands up, so the set the gate opened on can never be a
  // different set from the one the request carries. `hasAttachedEvidence` reads
  // this immutable `{video, images[]}` shape, never the live `images` state.
  const files = buildEvidenceFiles({video, images})
  const attachedEvidence = hasAttachedEvidence(files)

  const gaps = gapsFor({
    level, subcat, covered, blocked: partition.blocked,
    deadKg: Number(dead), volKg: vol, chargeKg: charge, packId, declared,
    // Request-level, and it clears the per-row evidence gap for every covered
    // row — see the semantics note above `gapsFor` in ./request.
    attachedEvidence,
  })
  const sentence = gapSentence(gaps)
  const scope = evidenceScope({
    coveredCount: covered.length, blockedCount: partition.blocked.length, attached: attachedEvidence,
  })

  const subOptions = React.useMemo(() => {
    const set = new Set(rows.map(subOf))
    return Array.from(set).sort()
  }, [rows])

  const mpws = covered.map((r) => r.mpw)
  const mpwLo = mpws.length ? Math.min(...mpws) : 0
  const mpwHi = mpws.length ? Math.max(...mpws) : 0

  // The LABEL is unchanged by an attachment: those rows still have nothing of
  // the merchant's own on file, and a video riding along on the request does not
  // become a scanner record against a row key. Only the TONE moves — an
  // attachment stops the shortfall being a blocker.
  const statusTone = partition.blocked.length
    ? (attachedEvidence ? 'warn' : 'danger')
    : partition.weak.length ? 'warn' : 'ok'
  const statusLabel = partition.blocked.length
    ? `${int(partition.blocked.length)} of ${int(covered.length)} ${rowWord(covered.length)} missing your evidence`
    : partition.weak.length
      ? `${int(partition.weak.length)} of ${int(covered.length)} ${rowWord(covered.length)} missing a sorter reading`
      : 'All rows ready to freeze'

  // Validation is `state/dwsUpload.js`'s own — the same format and size rules
  // the Evidence Studio applies, so a clip this drawer accepts is a clip that
  // one would have accepted too.
  const setFileError = (key, message) => setFileErrors((prev) => {
    const next = {...prev}
    if (message) next[key] = message; else delete next[key]
    return next
  })

  const takeVideo = (file) => {
    const check = validateVideo(file)
    if (!check.ok) {
      setFileError('video', check.message)
      return
    }
    setFileError('video', '')
    setVideo((prev) => {
      if (prev) releaseUrl(prev.url)
      return {name: file.name, size: file.size, url: trackUrl(file), file}
    })
  }

  const clearVideo = () => setVideo((prev) => {
    if (prev) releaseUrl(prev.url)
    return null
  })

  // Replacing a file in a slot has to revoke the url the old one held, or that
  // blob stays alive for the life of the tab.
  const takeImage = (slotKey, file) => {
    const check = validateImage(file)
    if (!check.ok) {
      setFileError(slotKey, check.message)
      return
    }
    setFileError(slotKey, '')
    setImages((prev) => {
      if (prev[slotKey]) releaseUrl(prev[slotKey].url)
      return {...prev, [slotKey]: {name: file.name, size: file.size, url: trackUrl(file), file}}
    })
  }

  const clearImage = (slotKey) => setImages((prev) => {
    if (!prev[slotKey]) return prev
    releaseUrl(prev[slotKey].url)
    const next = {...prev}
    delete next[slotKey]
    return next
  })

  const pickFrom = (event) => {
    const file = event.target.files && event.target.files[0]
    event.target.value = ''
    return file || null
  }

  // One `dragKey` for all six zones, keyed-compared: that is what stops the
  // pointer crossing from one slot into its neighbour leaving both lit.
  const dragOver = (key) => (event) => { event.preventDefault(); setDragKey(key) }
  const dragLeave = (key) => (event) => {
    event.preventDefault()
    setDragKey((k) => (k === key ? null : k))
  }
  const dropOn = (key, take) => (event) => {
    event.preventDefault()
    setDragKey(null)
    const file = event.dataTransfer.files && event.dataTransfer.files[0]
    if (file) take(file)
  }

  const pages = Math.max(1, Math.ceil(rows.length / PER_PAGE))
  const clampedPage = Math.min(page, pages - 1)
  const pageRows = rows.slice(clampedPage * PER_PAGE, clampedPage * PER_PAGE + PER_PAGE)

  function markTouched(setter) {
    return (event) => { setter(event.target.value); setTouched(true) }
  }

  // Named `takePackDims`, not `usePackDims`: a `use`-prefixed function called
  // from an onClick trips react-hooks/rules-of-hooks. Same helper, same name,
  // as the new-product drawer's.
  const takePackDims = () => {
    const [l, b, h] = pack.dims
    setLen(String(l)); setBre(String(b)); setHgt(String(h)); setTouched(true)
  }

  function handleSubmit() {
    // Re-checked here, not just at the disabled-button boundary: the button
    // can be reached by keyboard while state is mid-update.
    if (gaps.length) return

    // The urls now belong to the request the store is about to hold, so stop
    // tracking them here — this drawer unmounts the moment `InitiatePage`
    // clears the selection, and its cleanup would otherwise revoke urls the
    // request still points at. `files` is the set shaped for this render, the
    // same one the gate above opened on.
    const handedOn = [files.video && files.video.url].concat(files.images.map((f) => f.url)).filter(Boolean)
    blobUrls.current = blobUrls.current.filter((u) => !handedOn.includes(u))

    onSubmit({
      level,
      subject: subjectFor(level, subcat, covered),
      deadKg: Number(dead),
      volKg: vol,
      chargeKg: charge,
      packId,
      covers: covered.map((r) => r.key),
      // The provenance of `volKg`, now that it is derived rather than typed:
      // without it a reviewer sees a volumetric weight that can differ from the
      // declared material's own, with nothing on the request saying why. Same
      // field, same meaning, as the new-product drawer's.
      declaredDims: dimsTriple,
      // Always none: the section that let a merchant tick DWS records onto a
      // request is gone. Sent rather than omitted because `raisedWith` reads
      // `evidence` and the reviewer screens read `evidenceAwbs` — see
      // ./request — and both would see `undefined` if the keys dropped out.
      evidence: 0,
      evidenceAwbs: [],
      // Counted apart from `evidence` all the same: these are the merchant's
      // own readings, and folding them into that count would claim scanner
      // records that do not exist — see `raisedWith` in ./request.
      evidenceFiles: files,
      // The File objects themselves, for the live upload (data/api.js#apiUploadEvidence). Never
      // stored: `evidenceFiles` above is what a request keeps, this rides only to the submit handler.
      evidenceRaw: rawEvidenceFiles({video, images}),
      remarks,
    })
  }

  return (
    <Drawer anchor='right' open={open} onClose={onClose} sx={{'& .MuiDrawer-paper': {width: {xs: '100%', sm: 660, md: 820}, maxWidth: '100%'}}}>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <Box sx={{p: 2, borderBottom: `1px solid ${BORDER}`, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
          <Box sx={{minWidth: 0}}>
            <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
              Raise a weight freeze request
            </Typography>
            <Typography sx={labelSx}>
              {int(rows.length)} {rowWord(rows.length)} selected
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label='Close, without raising a request' size='small'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Box>

        <Box sx={{p: 2, overflowY: 'auto', flex: 1, display: 'grid', gap: GAP.lg}}>
          {/* 1. Packing-material warning — non-dismissible, permanent. */}
          <Alert severity='warning' sx={alertSx}>
            Every weight freeze is conditional on the declared packing material being used. Wrong packing
            material changes the weight materially, and the freeze does not hold.
          </Alert>

          {/* 2. Rows in this request. */}
          <Box>
            <Typography sx={sectionLabelSx}>Rows in this request ({int(rows.length)})</Typography>
            <Box sx={{border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden'}}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx}>SKU</TableCell>
                    <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Shipments</TableCell>
                    <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Most probable</TableCell>
                    <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Dead</TableCell>
                    <TableCell sx={{...headerCellSx, textAlign: 'right'}}>Volumetric</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell sx={drawerCellSx}>
                        <Typography
                          title={row.label}
                          sx={{
                            fontFamily: 'monospace', fontWeight: WEIGHT.bold, fontSize: SIZE['2xs'], color: NAVY,
                            maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                          }}
                        >
                          {row.label}
                        </Typography>
                        <Typography sx={subtleTextSx}>{row.sub}</Typography>
                      </TableCell>
                      <TableCell sx={{...drawerCellSx, textAlign: 'right'}}>{int(row.ships)}</TableCell>
                      <TableCell sx={{...drawerCellSx, textAlign: 'right'}}>{kg(row.mpw)}</TableCell>
                      <TableCell sx={{...drawerCellSx, textAlign: 'right'}}>{kg(row.deadKg)}</TableCell>
                      <TableCell sx={{...drawerCellSx, textAlign: 'right'}}>{kg(row.volKg)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {rows.length > PER_PAGE && (
                <TablePagination
                  component='div'
                  rowsPerPageOptions={[PER_PAGE]}
                  count={rows.length}
                  rowsPerPage={PER_PAGE}
                  page={clampedPage}
                  onPageChange={(event, next) => setPage(next)}
                  // The count and the page buttons take theme.typography.body2
                  // on their own, which left them larger than the table they
                  // page through.
                  sx={{
                    '& .MuiTablePagination-toolbar': {fontFamily: FONT, minHeight: 40},
                    '& .MuiTablePagination-displayedRows, & .MuiTablePagination-selectLabel': {
                      fontFamily: FONT, fontSize: SIZE['2xs'],
                    },
                  }}
                />
              )}
            </Box>
          </Box>

          {/* 3. Divergence warning. */}
          {covered.length > 1 && mpwHi > mpwLo && (
            <Alert severity='warning' sx={alertSx}>
              These rows do not share one most probable weight. They run from {kg(mpwLo)} to {kg(mpwHi)}.
              One frozen weight will over-bill the lighter rows and under-recover on the heavier ones, so
              consider splitting them into separate requests.
            </Alert>
          )}

          {/* 4. Freeze at which level. */}
          <Box component='fieldset' sx={{border: `1px solid ${BORDER}`, borderRadius: '10px', p: 1.5, m: 0}}>
            <Typography component='legend' sx={sectionLabelSx}>Freeze at which level</Typography>
            <RadioGroup
              value={level}
              onChange={(event) => setLevel(event.target.value)}
              sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: 1}}
            >
              {LEVELS.map((lv) => {
                const label = lv.l + (lv.v === FREEZE_LEVEL.SUBCAT ? ' (recommended)' : '')
                return (
                  <Box
                    key={lv.v}
                    sx={{border: `1px solid ${level === lv.v ? PRIMARY_BLUE : BORDER}`, borderRadius: '8px', p: 1}}
                  >
                    <FormControlLabel
                      value={lv.v}
                      control={<Radio size='small' />}
                      label={<Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE.xs, color: NAVY}}>{label}</Typography>}
                      sx={{m: 0, alignItems: 'flex-start'}}
                    />
                    <Typography sx={{...labelSx, ml: 3.5, display: 'block'}}>{lv.why}</Typography>
                  </Box>
                )
              })}
            </RadioGroup>
          </Box>

          {/* 5. Sub-category (Sub-category level only). */}
          {level === FREEZE_LEVEL.SUBCAT && (
            <Box sx={{display: 'grid', gap: 1}}>
              <TextField
                select size='small' label='Sub-category' value={subcat} required
                helperText='Taken from the sub-categories of the rows you selected.'
                onChange={(event) => setSubcat(event.target.value)}
                sx={{...fieldSx, maxWidth: 320}}
                SelectProps={{MenuProps: selectMenuProps}}
              >
                {subOptions.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              {subOptions.length > 1 && (
                <Alert severity='info' sx={alertSx}>
                  Your selection spans {int(subOptions.length)} sub-categories. A sub-category freeze covers
                  one at a time, so this request covers the {int(covered.length)} of {int(rows.length)}{' '}
                  {rowWord(rows.length)} in {subcat}.
                </Alert>
              )}
              {subcat === 'Uncategorised' && (
                <Alert severity='warning' sx={alertSx}>
                  &apos;Uncategorised&apos; is a catalogue placeholder, not a real sub-category, so a
                  sub-category freeze here would reach everything still uncategorised. Map these SKUs in
                  Catalogue first, or raise this one at SKU level.
                </Alert>
              )}
            </Box>
          )}

          {/* 6. Packing material — the choice, and what the catalogue holds
              against it. Before the weights, because the material is the
              condition the whole freeze hangs on and because the box measured
              below is the box this material makes. */}
          <Box>
            <Typography sx={sectionLabelSx}>Packing material</Typography>
            <TextField
              select size='small' label='Packing material used for these shipments' value={packId} required
              helperText='Mandatory. The freeze is granted against this material and nothing else.'
              onChange={markTouched(setPackId)}
              sx={{...fieldSx, minWidth: 340}}
              SelectProps={{MenuProps: selectMenuProps}}
            >
              {/* Branding rides on the option, not just on the card below: two
                  materials can share a shape and differ only in whether they
                  carry the merchant's own print. */}
              {packs.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {packOptionLabel(p)}
                </MenuItem>
              ))}
            </TextField>

            {/* Facts only, and only the ones that are the MATERIAL's own. This
                card used to carry a dimension triple, a volumetric weight and an
                outline of the box — a second parcel measured, weighed and drawn
                in the same drawer as the one the merchant is describing below,
                with only one of the two driving the bill. The dimensions live
                with the product weight now, and the drawing went with them. */}
            {pack && (
              <Box sx={{border: `1px solid ${BORDER}`, borderRadius: '10px', p: 1.5, mt: 1.5, maxWidth: {sm: 420}}}>
                <KeyValue items={[
                  {label: 'Type', value: PACK_KIND_LABEL[pack.kind] || pack.kind},
                  {label: 'Branding', value: brandingLabel(pack.branding)},
                  {label: 'Self weight', value: grams(pack.selfWeightG)},
                ]}
                />
              </Box>
            )}
          </Box>

          {/* 7. Product weight and dimensions, in the order the figures depend
              on each other: the product's own dead weight, then the box it
              ships in, then the two the courier derives from those. */}
          <Box>
            <Typography sx={{...sectionLabelSx, mb: 1.25}}>Product weight and dimensions</Typography>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: {xs: '1fr', sm: 'auto minmax(0, 1fr)'},
              gap: 2,
              alignItems: 'start',
            }}
            >
              {/* The parcel these figures describe, drawn from the triple beside
                  it, so correcting a side redraws it. A box that is not yet
                  complete gets the sleeve rather than an empty slot that appears
                  from nowhere on the third keystroke. */}
              <Box sx={{flexShrink: 0, width: 96, textAlign: 'center'}}>
                {vol > 0 ? <CartonSVG dims={dimsTriple} size={96} /> : <SleeveSVG size={96} />}
                <Typography sx={{...labelSx, display: 'block', mt: 0.25}}>Not to scale</Typography>
              </Box>

              <Box sx={{display: 'grid', gap: 1.5, minWidth: 0}}>
                {/* `required` on all four: they are what the freeze is granted
                    at, and a field that blocks the ask has to say so here rather
                    than only in the footer. MUI appends " *" to the accessible
                    name it derives from `label`, so a test querying these by
                    exact label has to match the asterisk too. The units are end
                    adornments rather than label text, so the labels read as the
                    quantities they name at the width four fields leave them. */}
                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(4, 1fr)'}, gap: 1.5}}>
                  <TextField
                    required
                    label='Dead weight' size='small' type='number' value={dead}
                    onChange={markTouched(setDead)} inputProps={{step: 0.05, min: 0}}
                    InputProps={{endAdornment: (
                      <InputAdornment position='end'><Typography sx={labelSx}>kg</Typography></InputAdornment>
                    )}}
                    sx={fieldSx}
                  />
                  {DIMENSION_FIELDS.map(([label, value, set]) => (
                    <TextField
                      key={label}
                      required
                      label={label} size='small' type='number' value={value}
                      onChange={markTouched(set)} inputProps={{step: 1, min: 0}}
                      InputProps={{endAdornment: (
                        <InputAdornment position='end'><Typography sx={labelSx}>cm</Typography></InputAdornment>
                      )}}
                      sx={fieldSx}
                    />
                  ))}
                </Box>
                <Typography sx={{...labelSx, display: 'block'}}>
                  Dead weight is the product on a scale, on its own. Measure the outside of the parcel as it
                  ships, packing material included.
                </Typography>

                {/* The two the courier works out, stated as results rather than
                    fields. 'Not yet' rather than 0.00 kg while the box is
                    incomplete: a zero here would read as a measurement. */}
                <Box sx={{
                  display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)'}, gap: 1.5,
                  p: 1.25, borderRadius: '10px', bgcolor: withOpacity(NAVY, 0.03),
                }}
                >
                  <Typography sx={labelSx}>
                    Volumetric weight{' '}
                    <Box component='span' sx={{color: NAVY, fontWeight: WEIGHT.semibold}}>
                      {vol > 0 ? kg(vol) : 'Not yet'}
                    </Box>
                    <Box component='span' sx={{display: 'block'}}>
                      Length × breadth × height ÷ {int(getVolumetricDivisor())}.
                    </Box>
                  </Typography>
                  <Typography sx={labelSx}>
                    Chargeable weight{' '}
                    <Box component='span' sx={{color: NAVY, fontWeight: WEIGHT.semibold}}>
                      {charge > 0 ? kg(charge) : 'Not yet'}
                    </Box>
                    <Box component='span' sx={{display: 'block'}}>
                      The higher of dead and volumetric. What the freeze bills at.
                    </Box>
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* `facts.higher > 0` is load-bearing: with the box still empty and
                the dead weight cleared, the chip would otherwise state that the
                two are equal at 0.00 kg — a claim about a parcel nobody has
                measured. */}
            {facts.higher > 0 && (
              <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1}}>
                <Chip
                  size='small'
                  label={GOVERNS_SENTENCE[facts.governs](facts.higher)}
                  // The app theme title-cases chip labels — on the inner span, so
                  // the override has to reach it there — and that turns this
                  // sentence's unit into 'Kg'. It is a sentence carrying a
                  // measurement, not a status word, so it opts out.
                  sx={{
                    ...chipSx(facts.governs === 'vol' ? 'warn' : 'info'),
                    '& .MuiChip-label': {textTransform: 'none'},
                  }}
                />
                {/* The figure these rows actually billed at. Without it the
                    merchant cannot see how far the proposal has moved from what
                    the history shows. */}
                {covered.length > 0 && (
                  <Typography sx={labelSx}>
                    Most probable weight across the covered {rowWord(covered.length)} is{' '}
                    {kg(weightedAvg(covered, 'mpw'))}.
                  </Typography>
                )}
              </Box>
            )}

            {/* Says out loud why nothing was pre-filled. A flyer or a paper wrap
                declares no box, and the pack card above no longer carries a
                dimensions row to say so. */}
            {pack && !pack.dims && (
              <Typography sx={{...labelSx, display: 'block', mt: 1.5}}>
                {pack.label} declares no box of its own, so the volumetric weight here comes only from the
                dimensions you enter.
              </Typography>
            )}

            {/* The reconciliation the old 'Use this pack's volumetric weight'
                button did, now that the box rather than the figure is what the
                merchant holds. Volumetric is anchored to the material, so a
                triple that disagrees with the declared one is worth one click to
                resolve — in either direction, since the merchant may well have
                measured a parcel the catalogue's own figure does not describe. */}
            {pack && pack.volKg != null && !withinTolerance(vol, pack.volKg) && (
              <Button size='small' sx={{...secondaryBtnSx, mt: 1.5}} onClick={takePackDims}>
                Use {pack.label}&apos;s own box, {dims(pack.dims)}
              </Button>
            )}
          </Box>

          {/* 8. The declaration confirms every figure above it — the material,
              the product weight and the box — so it sits after all of them
              rather than inside the packing section, which was only the first of
              them. Boxed and tinted on tick, because this is a required gate
              rather than a preference: an unticked checkbox in a long form is
              easy to read past, and its own footer gap sentence is the last
              thing a merchant sees before a disabled Submit. */}
          <Box
            sx={{
              px: 1.5, py: 0.5, borderRadius: '10px',
              border: `1px solid ${declared ? SUCCESS : BORDER}`,
              bgcolor: declared ? withOpacity(SUCCESS, 0.08) : 'transparent',
            }}
          >
            <FormControlLabel
              sx={{display: 'flex', alignItems: 'flex-start', m: 0}}
              control={<Checkbox checked={declared} onChange={(event) => setDeclared(event.target.checked)} />}
              label={<Typography sx={{...labelSx, mt: 0.9}}>{DECLARATION}</Typography>}
            />
          </Box>

          {/* 9. DWS data on file. Hidden outright when the level change has left
              the request covering nothing: a "0 of 0" partition reads as
              "all rows ready", which is the opposite of what is true. The
              footer's NO_ROWS gap is what speaks for that state. */}
          {covered.length > 0 && (
          <Box>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1}}>
              <Typography sx={sectionLabelSx}>DWS data on file</Typography>
              <Chip size='small' label={statusLabel} sx={chipSx(statusTone)} />
            </Box>

            {partition.blocked.length > 0 && !attachedEvidence && (
              <Alert severity='error' sx={{...alertSx, mb: 1}}>
                {int(partition.blocked.length)} of {int(covered.length)} {rowWord(covered.length)} have no
                evidence of your own on file, so Prozo has nothing of yours to argue the frozen weight from.
                The courier&apos;s sorter images alone show what was measured, not what was packed. Record it
                in the next step — a packing video or photos, attached to this request — or drop{' '}
                {partition.blocked.length === 1 ? 'it' : 'them'} from this freeze.
              </Alert>
            )}
            {partition.blocked.length > 0 && attachedEvidence && (
              <Alert severity='info' sx={{...alertSx, mb: 1}}>
                {int(partition.blocked.length)} of {int(covered.length)} {rowWord(covered.length)} still have
                no evidence of their own on file. What you attached below answers for the request as a whole,
                which is enough to submit it.
              </Alert>
            )}
            {partition.weak.length > 0 && (
              <Alert severity='warning' sx={{...alertSx, mb: 1}}>
                {int(partition.weak.length)} of {int(covered.length)} {rowWord(covered.length)} have your
                evidence but no courier sorter reading to check it against. The freeze can still be
                submitted, but Prozo argues it from the pair, so approval is slower and less likely.
              </Alert>
            )}

            <IniGateList rows={partition.rows} />
          </Box>
          )}

          {/* 10. Add evidence — the same two steps, and the same five named
              shots, the Evidence Studio takes.

              This is now the ONLY place a merchant can record their own
              readings for a freeze, so it is what clears the evidence gap when
              a covered row has nothing on file. Whether it is required or
              merely welcome is `evidenceScope`'s call, and so is the sentence
              stating what one attachment actually covers — a multi-row request
              gets told outright that it is one sample behind one proposed
              weight, not each row evidenced separately. */}
          <Box>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1}}>
              <Typography sx={sectionLabelSx}>Add evidence</Typography>
              <Chip size='small' label={scope.chip} sx={chipSx(EVIDENCE_TONE[scope.state])} />
            </Box>
            <Typography sx={{...labelSx, mb: scope.note ? 0.75 : 1.5}}>
              Readings you record here travel with the request. They are argued the same way a weight
              dispute is — a video, or stills, against the scanner&apos;s own reading.
            </Typography>
            {scope.note && (
              <Typography sx={{...labelSx, mb: 1.5, display: 'block'}}>{scope.note}</Typography>
            )}

            <Box sx={{display: 'grid', gap: 2}}>
              <Box>
                <StepHead n={1} title='Packing video' chip='Recommended' />
                <DropZone
                  icon={VideocamOutlinedIcon}
                  big
                  label='Add a packing video'
                  hint={`One unbroken clip, ending on the scale. ${VIDEO_FORMATS} · up to ${mb(DWS_MAX_VIDEO_BYTES)}.`}
                  file={video}
                  error={fileErrors.video}
                  dragActive={dragKey === 'video'}
                  accept={Object.keys(DWS_VIDEO_TYPES).join(',')}
                  ariaLabel='Upload the packing video'
                  removeAriaLabel='Remove the packing video'
                  onPick={(event) => { const f = pickFrom(event); if (f) takeVideo(f) }}
                  onClear={clearVideo}
                  onDragOver={dragOver('video')}
                  onDragLeave={dragLeave('video')}
                  onDrop={dropOn('video', takeVideo)}
                />
              </Box>

              <Box>
                <StepHead n={2} title='Photo evidence' />
                <Typography sx={{...labelSx, mb: 1}}>
                  Five shots, one fact each. Keep the measuring device and the parcel in the same frame — a
                  number with no parcel beside it proves nothing. {IMAGE_FORMATS} · up to{' '}
                  {mb(DWS_MAX_IMAGE_BYTES)} each.
                </Typography>
                <Box sx={{display: 'grid', gridTemplateColumns: {xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)'}, gap: 1}}>
                  {DWS_SLOTS.map((slot) => (
                    <DropZone
                      key={slot.k}
                      icon={ImageOutlinedIcon}
                      label={slot.label}
                      hint={slot.hint}
                      file={images[slot.k]}
                      error={fileErrors[slot.k]}
                      dragActive={dragKey === `img:${slot.k}`}
                      accept={Object.keys(DWS_IMAGE_TYPES).join(',')}
                      ariaLabel={`Upload the ${slot.label.toLowerCase()} image`}
                      removeAriaLabel={`Remove the ${slot.label.toLowerCase()} image`}
                      onPick={(event) => { const f = pickFrom(event); if (f) takeImage(slot.k, f) }}
                      onClear={() => clearImage(slot.k)}
                      onDragOver={dragOver(`img:${slot.k}`)}
                      onDragLeave={dragLeave(`img:${slot.k}`)}
                      onDrop={dropOn(`img:${slot.k}`, (f) => takeImage(slot.k, f))}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* 11. Remarks. */}
          <TextField
            label='Add a note for the reviewer (optional)' size='small' multiline minRows={3} fullWidth
            helperText='Anything Prozo should know: a packing change, a seasonal variant, a known outlier.'
            value={remarks} onChange={(event) => setRemarks(event.target.value)}
            sx={fieldSx}
          />

          {/* 12. Closing note. The two sentences before it are the standing rule
              — a raise is a proposal, and approval never outlives the declared
              packing material; the sentence itself is POLICY.preliminary,
              verbatim from the source document rather than paraphrased here. */}
          <Alert severity='info' sx={alertSx}>
            A merchant-raised freeze takes effect only after Prozo approves it, and approval is always
            conditional on the packing material declared above. {POLICY.preliminary}
          </Alert>
        </Box>

        <Box sx={{p: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap'}}>
          <Typography sx={{...labelSx, flex: '1 1 260px', color: gaps.length ? WARNING : SUBTLE}}>
            {gaps.length
              ? sentence
              : `Goes to Prozo for review as ${level.toLowerCase()} level, covering `
                + `${int(covered.length)} ${rowWord(covered.length)}.`}
          </Typography>
          <Button sx={secondaryBtnSx} onClick={onClose}>Cancel</Button>
          {gaps.length ? (
            <Tooltip title={sentence}>
              <Box component='span' tabIndex={0} role='note' aria-label={'Submit is unavailable. ' + sentence}>
                <Button variant='contained' disabled sx={primaryBtnSx} startIcon={<AcUnitOutlinedIcon sx={{fontSize: 15}} />}>
                  Submit for Prozo review
                </Button>
              </Box>
            </Tooltip>
          ) : (
            <Button variant='contained' sx={primaryBtnSx} startIcon={<AcUnitOutlinedIcon sx={{fontSize: 15}} />} onClick={handleSubmit}>
              Submit for Prozo review
            </Button>
          )}
        </Box>
      </Box>
    </Drawer>
  )
}
